import React, {
  useState,
  useCallback,
  useMemo,
  ChangeEvent,
  MouseEvent,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Button,
  Paper,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link } from 'react-router-dom';
import UserBadge from './UserBadge';
import SearchInput from '../SearchInput';
import { MoreVert, FilterList, AddOutlined } from '@mui/icons-material';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import moment from 'moment';
import { DATE_FORMAT_SPACED } from '../../helpers/dates';
import { User } from '../../model/types';
import { getUsers, deleteUser } from '../../store/users.actions';
import * as selectors from '../../store/selectors';

const useStyles = makeStyles((theme: Theme) => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
    paddingTop: theme.spacing(3),
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      alignItems: 'flex-end',
    },
  },
  toolbarFilters: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexGrow: 1,
    width: '100%',
    marginRight: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      alignSelf: 'center',
      flexBasis: '50%',
    },
  },
  addUserButton: {
    color: 'white',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '16px',
    },
  },
  filtersButton: {
    marginRight: theme.spacing(1),
  },
  filterSet: {
    padding: '24px',
    position: 'absolute',
    top: '120%',
    left: '0',
    zIndex: 10,
  },
  filtersLabel: {
    marginBottom: '8px',
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
  tableRow: {
    transition: 'all 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: '#F6F9FD',
    },
  },
}));

export const UsersList = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const users = useSelector(selectors.getUsers);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [filteredData, setFilteredData] = useState<User[]>([]);
  const [selectedDesignations, setSelectedDesignations] = useState<string[]>(
    []
  );
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const handleUserDeleteClick = useCallback(
    (userId: string) => {
      dispatch(deleteUser(userId));
    },
    [dispatch]
  );

  const handleChangePage = useCallback(
    (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
      setPage(newPage);
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const filterData = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const searchQuery = event.target.value.toLowerCase();
      let data: User[] = [];

      if (searchQuery === '' || searchQuery === null)
        return setFilteredData([]);

      data = users.filter(user =>
        Object.values(user).join(' ').toLowerCase().includes(searchQuery)
      );

      return setFilteredData(data);
    },
    [users]
  );

  const handleFilterShowChange = useCallback(
    () => setShowFilters(!showFilters),
    [showFilters]
  );

  const handleDesignationFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const designation = event.target.name;
      let filteredDesignations = [...selectedDesignations];

      if (selectedDesignations.includes(designation)) {
        let index = selectedDesignations.indexOf(designation);

        if (index !== -1) {
          filteredDesignations.splice(index, 1);
          return setSelectedDesignations(filteredDesignations);
        }
      }

      filteredDesignations.push(designation);
      return setSelectedDesignations(filteredDesignations);
    },
    [selectedDesignations]
  );

  const uniqueDesignations = useMemo(
    () => [...new Set(users.map((user: User) => user.designation))],
    [users]
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const filterDataBySelectedDesignations = useCallback(() => {
    let dataToFilter = filteredData.length > 0 ? [...filteredData] : users;

    return setFilteredData(
      dataToFilter.filter(user =>
        selectedDesignations.includes(user.designation)
      )
    );
  }, [filteredData, users, selectedDesignations]);

  // useEffect(() => {
  //   filterDataBySelectedDesignations();
  // }, [filterDataBySelectedDesignations]);

  return (
    <>
      {users && users.length > 0 && (
        <div>
          <div className={classes.toolbar}>
            <div className={classes.toolbarFilters}>
              <Button
                className={classes.filtersButton}
                color="primary"
                variant="contained"
                startIcon={<FilterList />}
                onClick={handleFilterShowChange}
              >
                {t('filters')}
              </Button>

              {uniqueDesignations.length > 0 && showFilters && (
                <Paper className={classes.filterSet}>
                  <FormControl
                    variant="standard"
                    component="fieldset"
                    // className={classes.formControl}
                  >
                    <FormGroup>
                      <FormLabel
                        className={classes.filtersLabel}
                        focused={false}
                      >
                        Designation
                      </FormLabel>
                      {uniqueDesignations.map((designation: string) => (
                        <FormControlLabel
                          key={designation}
                          control={
                            <Checkbox
                              checked={selectedDesignations.includes(
                                designation
                              )}
                              onChange={event =>
                                handleDesignationFilterChange(event)
                              }
                              name={designation}
                              color="default"
                            />
                          }
                          label={designation}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                </Paper>
              )}

              <SearchInput
                // className={classes.searchInput}
                onChage={filterData}
                placeholder={t('Search_users')}
              />
            </div>

            <Link className={classes.link} to="/users/add">
              <Button
                className={classes.addUserButton}
                variant="contained"
                color="primary"
                startIcon={<AddOutlined />}
              >
                {t('Add_User')}
              </Button>
            </Link>
          </div>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {/*TODO Anvar: fix bold font for russian*/}
                    <TableCell>{t('Name')}</TableCell>
                    <TableCell align="left">{t('Email')}</TableCell>
                    <TableCell align="left">{t('Designation')}</TableCell>
                    <TableCell align="left">{t('Last_seen')}</TableCell>
                    <TableCell align="left">{t('Pass_Expiry_Date')}</TableCell>
                    <TableCell colSpan={2} align="left">
                      {t('Joining_Date')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(filteredData.length > 0 ? filteredData : users)
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(user => (
                      <TableRow key={user._id} className={classes.tableRow}>
                        <TableCell component="th" scope="row">
                          <UserBadge
                            firstName={user.firstName}
                            lastName={user.lastName}
                            imageUrl=""
                          />
                        </TableCell>
                        <TableCell align="left">{user.email}</TableCell>
                        <TableCell align="left">{user.designation}</TableCell>
                        <TableCell align="left">
                          {moment(user.lastSeen).format(DATE_FORMAT_SPACED)}
                        </TableCell>
                        <TableCell align="left">
                          {moment(user.passExpiryDate).format(
                            DATE_FORMAT_SPACED
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {moment(user.joinDate).format(DATE_FORMAT_SPACED)}
                        </TableCell>
                        <TableCell align="left">
                          <PopupState
                            variant="popover"
                            popupId="demo-popup-menu"
                          >
                            {popupState => (
                              <>
                                <IconButton
                                  aria-controls="user-row-menu"
                                  aria-haspopup="true"
                                  {...bindTrigger(popupState)}
                                  size="large"
                                >
                                  <MoreVert />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                  <MenuItem>
                                    <Link
                                      className={classes.link}
                                      to={`/users/edit/${user._id}`}
                                    >
                                      <Typography variant="inherit">
                                        {t('Edit')}
                                      </Typography>
                                    </Link>
                                  </MenuItem>
                                  <MenuItem
                                    onClick={() =>
                                      handleUserDeleteClick(user._id)
                                    }
                                  >
                                    <Typography variant="inherit">
                                      {t('Delete')}
                                    </Typography>
                                  </MenuItem>
                                </Menu>
                              </>
                            )}
                          </PopupState>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </div>
      )}
    </>
  );
};
